<template>
  <table v-if="item">
    <thead>
      <tr><th class="title" colspan="5">Sensitivity Analysis (ROI)</th></tr>
      <tr>
        <th class="current">Hashrate<br/>Price\</th>
        <th :class="{ current: r === 1 }" v-for="[r, h] of hashrates" :key="`s-${r}`">{{h / blockchain[coin].totalUnitFactor | price('', 2)}} {{blockchain[coin].totalUnit}}<div>{{r}}x</div></th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="[r, p] of prices" :key="`p-${r}`">
        <th :class="{ current: r === 1 }">{{p | price('USD', 2)}} <div v-if="r === 1">(current)</div></th>
        <sensitivity-cell :class="{ current: r === 1 && hr === 1 }" v-for="[hr] of hashrates" :key="`r-${r}-${hr}`" :value="calculateRoi(p, hr)" />
      </tr>
    </tbody>
  </table>
</template>

<script>
import { mapState } from "vuex";

import SensitivityCell from "@/components/SensitivityCell";
import { price, hashrate, percent } from "@/filters";

const hashrateRatio = [0.5, 0.7, 1, 1.3, 1.5];
const priceRatio = [0.5, 0.75, 1, 1.5, 2];

function roundPrice(price, shouldRound = false) {
  if (!shouldRound) return price;

  let f = 1;
  while (price > 10) {
    price /= 10;
    f *= 10;
  }

  return Math.round(price * 2) / 2 * f;
}

export default {
  name: "SensitivityTable",
  props: ["item"],
  filters: { price, hashrate, percent },
  components: { SensitivityCell },
  computed: {
    ...mapState(["blockchain"]),
    coin() {
      return this.item.coin;
    },
    hashrates() {
      return hashrateRatio.map(r => [r, this.blockchain[this.coin].hashrate * r])
    },
    prices() {
      return priceRatio.map(r => [r, roundPrice(this.blockchain[this.coin].price * r, r !== 1)]);
    }
  },
  methods: {
    calculateRoi(price, hashrateRatio) {
      const priceRatio = price / this.blockchain[this.coin].price;
      const { duration } = this.item;
      const { halvingTs } = this.blockchain[this.coin];
      const preHalvingDays = halvingTs && halvingTs > Date.now() ? (halvingTs - Date.now()) / 86400000 : this.item.duration;

      let totalPayoff = 0, totalCost = this.item.total.upfront / this.item.size;
      for (let i = 1; i <= duration; i++) {
        const pr = 1 + (priceRatio - 1) * (i / duration);
        const hr = 1 + (hashrateRatio - 1) * (i / duration);
        const price = this.blockchain[this.coin].price * pr;

        let payoff = this.blockchain[this.coin].miningEarning / hr * price;
        if (i > preHalvingDays) {
          if (i > preHalvingDays + 1) {
            payoff /= 2;
          } else {
            payoff -= (i - preHalvingDays) * payoff / 2;
          }
        }

        const elecFee = this.item.daily.elecFee;
        const managementFee = Math.max(0, (payoff - elecFee) * this.item.managementFeeRate);

        const cost = Math.min(payoff, elecFee + managementFee);
        totalPayoff += payoff;
        totalCost += cost;
      }

      return totalPayoff / totalCost - 1;
    }
  }
}
</script>

<style scoped lang="scss">
table {
  width: 100%;
  border-spacing: 0;
  border-collapse: collapse;

  th {
    font-weight: normal;
  }

  th.title {
    font-size: 16px!important;
    text-align: left;
    padding-left: 32px;
    text-transform: uppercase!important;
  }

  .current {
    font-weight: bold;
  }

  th, td {
    font-size: 12px;
    text-align: center;
    margin: 0;
    padding: 10px 0;
    border: none;
  }

  tbody {
    td {
      line-height: 36px;
    }

    td.current {
      background-color: #fffacc;
    }

    th.current {
      line-height: 18px;
    }
  }
}
</style>